import {
  offTarget as airOffTarget,
  FULLSCREEN_OVERLAY_MODES,
  type ProjectState,
} from "@livcg/schema";
import { useRef, useState } from "react";
import { sendRaw } from "../../shared/sendRaw.js";
import { useToast } from "../../ui/index.js";
import { stagedExtraTime } from "../useStagedExtraTime.js";
import { activeGridMode, gridSideOf, showGridOnAir } from "./lineupAir.js";
import { type OverlayExtras, overlayDataFor, rosterModeHasData } from "./overlayData.js";

/** Cooldown after each click — covers the overlay's GSAP enter/exit (~500ms)
 *  so a tile can't re-fire mid-tween. Shared by Control, Classic and Deck. */
const COOLDOWN_MS = 500;

/** Fullscreen tableaux — the SKORE `table` radio (big/matchid/stadium). */
export const FULLSCREEN_MODES = FULLSCREEN_OVERLAY_MODES;

/** SHARED SKORE toggle semantics for the OverlayBoard and Deck overlay
 *  tiles. DeckView calls it once and passes props to the tiles. */
export function useOverlaySwitch(slug: string, state: ProjectState, extras: OverlayExtras) {
  const current = state.overlay.mode;
  const { toast } = useToast();
  /** GRID is not in `overlay.mode` — its on-air state is in the `lineup` replicant. */
  const gridActive = activeGridMode(state);
  const [busyMode, setBusyMode] = useState<string | null>(null);
  const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const arm = () => {
    if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    cooldownTimer.current = setTimeout(() => setBusyMode(null), COOLDOWN_MS);
  };

  /** The "Extra time" tile does three things in one click: writes the value
   *  staged in the stepper, turns on the strip under the clock and brings up
   *  the banner (the server retracts it by itself after 4.5s). All three are in a
   *  single `extraTime.show` message — sent separately, overlay.show would arrive
   *  before the value was written and the banner would be retracted immediately.
   *  The strip stays after the banner is gone: until the phase changes or the
   *  checkbox is turned off. */
  const fireExtraTime = () => {
    const staged = stagedExtraTime(slug, state.match.additionalTime);
    if (staged <= 0) return false; // no value — nothing to show
    void sendRaw("extraTime.show", { slug, additionalTime: staged });
    return true;
  };

  /** Taking a mode off air — the server applies the SKORE return rules
   *  (fullscreen → the air before it, little/titre → the scoreboard under it,
   *  otherwise empty) from the replicant, the same for every operator. */
  const sendOff = (mode: string) => void sendRaw("overlay.off", { slug, mode });

  /** What the air shows after `sendOff(mode)` — the Efir view previews a hide with it. */
  const offTarget = (mode: string): { mode: string | null; data: unknown } => {
    const t = airOffTarget(state.overlay, mode);
    return t ? { mode: t.mode, data: t.data ?? {} } : { mode: null, data: {} };
  };

  /** Is the tile on air — `lineup` for the grid, `overlay.mode` for the rest. */
  const isActive = (mode: string): boolean =>
    gridSideOf(mode) ? gridActive === mode : current === mode;

  /** GRID tile: not overlay.show but lineup.show/hide — the grid is a separate
   *  scene, it doesn't touch overlay.mode. */
  const toggleGrid = (mode: string, side: "home" | "away") => {
    setBusyMode(mode);
    if (gridActive === mode) {
      void sendRaw("lineup.hide", { slug });
    } else {
      // The server closes the on-air scoreboard/tableau in the SAME write as
      // lineup.show (a separate sendOff would make the scoreboard flicker).
      void showGridOnAir(slug, side, state).then((err) => {
        if (err) toast({ tone: "error", title: "Efirga uzatib bo'lmadi", description: err });
      });
    }
    arm();
  };

  const toggle = (mode: string) => {
    if (busyMode) return;
    const gridSide = gridSideOf(mode);
    if (gridSide) return toggleGrid(mode, gridSide);
    // If a tableau/scoreboard is pressed while the grid is on air, the server closes the
    // grid in the SAME write as overlay.show — a separate lineup.hide would make the
    // scoreboard flicker.
    // Data-signal guard (spec): overlay.show is NOT SENT for an empty roster/reserve
    // (otherwise the graphic hides the scoreboard and gives empty air).
    // Only in the ON direction — turning off a tile that is already on air is allowed.
    if (current !== mode && !rosterModeHasData(mode, state, extras)) return;
    setBusyMode(mode);
    if (current === mode) {
      sendOff(mode);
    } else {
      if (mode === "little_extra_time") {
        if (!fireExtraTime()) {
          setBusyMode(null);
          return;
        }
      } else {
        void sendRaw("overlay.show", {
          slug,
          mode,
          data: overlayDataFor(mode, state, extras),
        });
      }
    }
    arm();
  };

  /** Put on air / refresh — NOT a toggle: always overlay.show (with the
   *  latest data). After the custom text is edited the "Efirga/Yangilash" button
   *  updates it live with this. */
  const apply = (mode: string) => {
    if (busyMode) return;
    if (!rosterModeHasData(mode, state, extras)) return;
    setBusyMode(mode);
    if (mode === "little_extra_time") {
      if (!fireExtraTime()) {
        setBusyMode(null);
        return;
      }
    } else {
      void sendRaw("overlay.show", { slug, mode, data: overlayDataFor(mode, state, extras) });
    }
    arm();
  };

  const hideCurrent = () => {
    if (busyMode) return;
    // The grid is the topmost surface — if it's on air it goes first.
    if (gridActive) {
      setBusyMode(gridActive);
      void sendRaw("lineup.hide", { slug });
      arm();
      return;
    }
    if (!current) return;
    setBusyMode(current);
    // Esc / "Yashirish" have the same semantics as a tile toggle-off: the little,
    // titre or fullscreen goes — the scoreboard underneath stays. A second Esc
    // takes the scoreboard itself and empties the air (Shift+Esc = ALL OFF).
    sendOff(current);
    arm();
  };

  return { current, gridActive, isActive, busyMode, toggle, apply, hideCurrent, offTarget };
}
